import { useState } from 'react';
import type { FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/dah87sbp14a1h3mr6f2g';

type Status = 'idle' | 'submitting' | 'success' | 'error';

const categories = ['消费维权', '行业黑幕', '产品问题', '职场经历', '其他线索'];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }
    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (typeof value === 'string' && value.trim() !== '') {
        payload.append(key, value);
      }
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText) as Record<string, unknown>;
      } catch {
        parsed = null;
      }

      const meta = (parsed?.meta ?? {}) as Record<string, unknown>;
      const serverMsg = String(
        meta.message || parsed?.message || meta.detail || responseText || '',
      );
      const code = String(parsed?.code ?? '');
      const isSpam = serverMsg.toLowerCase().includes('spam');

      if (response.ok && code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交未成功，请稍后重试。');
    }
  };

  if (status === 'success') {
    return (
      <div className="rounded-lg border border-primary-200 bg-primary-50 p-8 text-center">
        <span className="w-14 h-14 mx-auto rounded-full bg-primary-500 text-background-50 flex items-center justify-center">
          <i className="ri-check-line text-3xl" aria-hidden="true" />
        </span>
        <h3 className="mt-4 font-heading text-xl font-bold text-foreground-950">线索已收到，感谢你的信任</h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-700">
          编辑部会逐条阅读。如果线索进入核实流程，我们会通过你留下的联系方式确认细节。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 inline-flex items-center gap-2 border border-primary-300 hover:bg-background-50 text-primary-700 text-sm font-label px-5 py-2.5 rounded-md transition-colors"
        >
          <i className="ri-add-line" aria-hidden="true" />
          再提交一条
        </button>
      </div>
    );
  }

  return (
    <form
      data-readdy-form
      id="contribute-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-300/60 bg-background-50 p-5 md:p-7"
      noValidate
    >
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
          <i className="ri-send-plane-fill" aria-hidden="true" />
        </span>
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground-950">投稿与联系表单</h3>
          <p className="text-xs font-label text-foreground-500">带 * 为必填项，正文限 500 字以内</p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-label text-foreground-700">称呼 *</span>
          <input
            type="text"
            name="name"
            required
            placeholder="如何称呼你"
            className="bg-background-100 border border-background-300/70 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-label text-foreground-700">邮箱 *</span>
          <input
            type="email"
            name="email"
            required
            placeholder="用于接收核实联系"
            className="bg-background-100 border border-background-300/70 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
          />
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-label text-foreground-700">线索类型 *</span>
          <select
            name="category"
            required
            defaultValue=""
            className="bg-background-100 border border-background-300/70 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
          >
            <option value="" disabled>
              请选择线索类型
            </option>
            {categories.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-label text-foreground-700">其他联系方式（选填）</span>
          <input
            type="text"
            name="contact"
            placeholder="手机 / 微信，便于进一步沟通"
            className="bg-background-100 border border-background-300/70 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
          />
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm font-label text-foreground-700">事情经过与可提供的材料 *</span>
        <textarea
          name="content"
          required
          maxLength={500}
          rows={5}
          placeholder="建议写清楚时间、地点、涉及方，以及你手上有的截图、记录或凭证说明。"
          className="bg-background-100 border border-background-300/70 rounded-md px-3.5 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 resize-none"
        />
      </label>

      <label className="mt-4 flex items-start gap-2.5 text-xs leading-relaxed text-foreground-600">
        <input
          type="checkbox"
          name="policy"
          required
          className="mt-0.5 accent-primary-500"
        />
        我确认以上内容基于本人真实经历或可查证信息，愿意配合编辑部核实。
      </label>

      <input
        type="text"
        name="website_alt"
        className="hp-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === 'error' && formError && (
        <p className="mt-4 flex items-start gap-2 rounded-md border border-primary-300 bg-primary-50 px-4 py-3 text-sm text-primary-800">
          <i className="ri-error-warning-line mt-0.5" aria-hidden="true" />
          {formError}
        </p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-6 inline-flex items-center justify-center gap-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed text-background-50 font-label px-6 py-3 rounded-md transition-colors whitespace-nowrap"
      >
        <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-fill'} aria-hidden="true" />
        {status === 'submitting' ? '正在提交…' : '提交线索'}
      </button>
      <p className="mt-3 text-xs text-foreground-500">
        提交即表示你同意编辑部在核实过程中与你联系。我们不会公开你的个人联系方式。
      </p>
    </form>
  );
}